import React, { useContext, useState } from 'react';
import './FoodItem.css';
import { assets } from '../../assets/assets';
import { StoreContext } from '../../Context/StoreContext';
import { useMultiCart } from '../../Context/MultiCartContext';
import { useTranslation } from 'react-i18next';

const FoodItem = ({ id, name, image, desc, price, restaurantName, restaurantId, isAvailable = true, restaurantStatus }) => {
    const { t } = useTranslation();
    const { currency, url } = useContext(StoreContext);
    const { restaurantCarts, addToRestaurantCart, removeFromRestaurantCart } = useMultiCart();
    const [isLiked, setIsLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(() => {
        // Generate a random like count for demonstration (5-25 likes)
        return Math.floor(Math.random() * 21) + 5;
    });

    // Format image URL correctly - same approach as PopularFoods
    const getImageUrl = (imgUrl) => {
        if (!imgUrl) {
            return assets.food_1; // fallback image
        }
        
        // If image is a full URL, use it directly
        if (imgUrl.startsWith('http')) return imgUrl;
        
        // If image is a relative path, append the server URL
        return `${url}/uploads/${imgUrl}`;
    };

    // Find the restaurant cart and check if item is in it
    const restaurantCart = restaurantCarts.find(cart => cart.restaurantId === restaurantId);
    const cartItem = restaurantCart?.items.find(item => item.foodId === id);
    const isInCart = cartItem && cartItem.quantity > 0;
    
    // Get the quantity of the item in the cart if it exists
    const getItemQuantity = () => {
        return cartItem ? cartItem.quantity : 0;
    };
    
    // Safely handle the price to ensure it's a number
    const safePrice = typeof price === 'number' ? price : 0;
    
    // Check if item can be ordered (both item availability and restaurant status)
    const canOrder = isAvailable && (restaurantStatus?.isOpen !== false) && restaurantId;
    
    // Prevent adding to cart if item is not available or restaurant is closed
    const handleAddToCart = async () => {
        if (canOrder) {
            await addToRestaurantCart(id, restaurantId, {
                name,
                price: safePrice,
                image,
                restaurantName,
                restaurantImage: null
            });
        }
    };

    const handleRemoveFromCart = async () => {
        if (canOrder) {
            await removeFromRestaurantCart(id, restaurantId);
        }
    };

    // Handle like functionality
    const handleLike = (e) => {
        e.stopPropagation();
        setIsLiked(!isLiked);
        // Update like count
        if (!isLiked) {
            setLikeCount(prev => prev + 1);
        } else {
            setLikeCount(prev => Math.max(0, prev - 1));
        }
    };

    return (
        <div className={`food-item ${!isAvailable ? 'food-item-unavailable' : ''}`}>
            <div className="food-item-image-container">
                <img 
                    src={getImageUrl(image)} 
                    alt={name || t('food.food_item')} 
                    className="food-item-image"
                />
                
                {/* Price Badge - Top Left */}
                <div className="food-item-price-badge">
                    {Math.round(safePrice)} {currency}
                </div>
                
                {/* Like Button with Count - Top Right */}
                <div className={`food-item-like ${isLiked ? 'liked' : ''}`} onClick={handleLike}>
                    <span className="heart-icon">{isLiked ? '❤️' : '🤍'}</span>
                    <span className="like-count">{likeCount}</span>
                </div>
                
                {!isAvailable && (
                    <div className="food-item-unavailable-overlay">
                        <div className="unavailable-content">
                            <span className="unavailable-text">{t('food.out_of_stock')}</span>
                        </div>
                    </div>
                )}
                
                {!isInCart && canOrder && (
                    <div className="food-item-add-btn" onClick={handleAddToCart}>
                        +
                    </div>
                )}
                {isInCart && canOrder && (
                    <div className="food-item-counter">
                        <img 
                            onClick={handleRemoveFromCart} 
                            src={assets.remove_icon_red} 
                            alt={t('buttons.remove_from_cart')} 
                        />
                        <p>{getItemQuantity()}</p>
                        <img 
                            onClick={handleAddToCart} 
                            src={assets.add_icon_green} 
                            alt={t('buttons.add_to_cart')} 
                        />
                    </div>
                )}
            </div>
            <div className="food-item-info">
                <h3 className="food-item-name">{name || t('food.unnamed_item')}</h3>
                <p className="food-item-desc">{desc || t('food.no_description')}</p>
                {restaurantName && (
                    <div className="food-item-restaurant">
                        <span className="restaurant-tag">{restaurantName}</span>
                    </div>
                )}
                {!canOrder && isAvailable && (
                    <div className="availability-status">
                        <span className="status-indicator restaurant-closed">
                            ● {t('restaurants.closed')}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FoodItem;